<script lang="ts">
import { ref, computed, onMounted, defineComponent } from 'vue';

import { useEventListener } from '@composables';
import { Promotion } from '@interfaces';
import { useStore } from '@store';
import { delay } from '@tools/delay';
import { isNumber } from '@tools/type-guards';

import ParallaxLayer, { Props } from '@components/ParallaxLayer.vue';
import StatusAlertBanner from '@components/StatusAlertBanner.vue';
import ZephyrLogo from '@components/SVGs/ZephyrLogo.vue';

import parallax1 from '@assets/images/parallax1.svg';
import parallax2 from '@assets/images/parallax2.svg';
import parallax3 from '@assets/images/parallax3.svg';
import parallaxForeground from '@assets/images/parallax-foreground.svg';
import parallaxDrone from '@assets/images/parallax-drone.png';

import MainSectionNavbarPromotions from './MainSectionNavbarPromotions.vue';

/** ... */
type LayerInstance = InstanceType<typeof ParallaxLayer>;

/**
 * ...
 */
interface ParallaxLayerConfig extends Props {
  className: string;
  key: string;
}

/** ... */
const PARALLAX_LAYER_CONFIGS: ParallaxLayerConfig[] = [
  {
    className: 'l1',
    key: 'l1',
    index: -4,
    depth: 0.25,
    yOffset: 200,
    bgSize: '130%',
    bgImage: parallax1,
  },
  {
    className: 'l2',
    key: 'l2',
    index: -3,
    depth: 0.5,
    yOffset: 220,
    bgSize: '130%',
    bgImage: parallax2,
  },
  {
    className: 'l3',
    key: 'l3',
    index: -2,
    depth: 0.75,
    yOffset: 200,
    bgSize: '130%',
    bgImage: parallax3,
  },
  {
    className: 'l4',
    key: 'l4',
    index: -1,
    depth: 1,
    yOffset: 200,
    bgSize: '130%',
    bgImage: parallaxForeground,
  },
  {
    className: 'l5',
    key: 'l5',
    index: -1,
    depth: 0.5,
    yOffset: 150,
    bgSize: '4%',
    bgPos: '59% 15%',
    bgImage: parallaxDrone,
  },
];

for (const item of PARALLAX_LAYER_CONFIGS) {
  if (isNumber(item.yOffset)) item.yOffset -= 150;
}

export default defineComponent({ name: 'MainSectionHero' });
</script>

<script setup lang="ts">
const parallaxLayers = ref<LayerInstance[]>([]);

const store = useStore();

const displayMode = computed(() => {
  return store.state.displayMode;
});

const showLogo = ref(false);

const promotions = computed(() => {
  const promos = store.state.promotions.items.map((promo) => ({
    id: promo.id,
    body: promo.body,
    title: promo.title,
    shortText: promo.shortText ?? null,
    navbarText: promo.navbarText ?? null,
    navbarTextMobile: promo.navbarTextMobile ?? null,
    navbarBackgroundColor: promo.navbarBackgroundColor ?? null,
    callToAction: promo.callToAction ?? null,
    url: promo.url ?? null,
    image: promo.image ?? null,
  })) as Promotion[];

  return promos.length ? promos : null;
});

useEventListener('load', updateSceneParallaxing);
useEventListener('resize', updateSceneParallaxing);
useEventListener('scroll', updateSceneParallaxing);

onMounted(() => {
  void getSiteStatusInfo();

  void delay(750).then(() => (showLogo.value = true));
});

async function getSiteStatusInfo() {
  const response = await fetch('https://status.zephyr-sim.com/feed.rss');

  const data = await response.text();

  console.log(data);
}

/**
 * ...
 */
function updateSceneParallaxing() {
  if (displayMode.value === 'desktop') {
    for (const layer of parallaxLayers.value ?? []) layer.updatePosition();
  }
}
</script>

<template>
  <section id="section-hero" :class="{ promos: promotions }">
    <StatusAlertBanner />

    <MainSectionNavbarPromotions v-if="promotions" v-bind="{ promotions }" />

    <TransitionGroup tag="div" class="scene-wrapper" appear>
      <ParallaxLayer
        v-for="{ className, key, ...props } in PARALLAX_LAYER_CONFIGS"
        :id="`hero-scene-${key}`"
        ref="parallaxLayers"
        :key="key"
        :class="className"
        v-bind="props"
      />
    </TransitionGroup>

    <div class="logo-wrapper">
      <Transition appear>
        <ZephyrLogo v-if="showLogo" id="hero-logo" animate />
      </Transition>

      <div id="logo-tagline">
        <h2>Cutting Edge Drone Simulator</h2>
      </div>

      <b-button
        id="logo-tagline"
        variant="alt-primary"
        :to="{ name: 'individuals' }"
      >
        Purchase
      </b-button>
    </div>
  </section>
</template>

<style scoped lang="scss">
@keyframes tagline {
  0% {
    opacity: 0;
    transform: translateX(-10px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

#section-hero {
  position: relative;
  height: 65vh;
  padding-bottom: 0;
  justify-content: flex-start;
  background: transparent !important;
  overflow: hidden;
  padding: var(--navbar-height) 0 0 0;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);

  @include app-mobile {
    height: 50vh;
  }

  @media screen and (max-width: 600px) {
    height: 40vh !important;
  }

  &::after {
    content: '';
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -5;
    background: radial-gradient(#fffbd4, #f1da36, #f1da36);
  }

  &.promos {
    .logo-wrapper {
      margin-top: calc(200px - var(--navbar-height));
    }
  }
}

.logo-wrapper {
  position: relative;
  margin-top: 120px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  #logo-tagline {
    // opacity: 0;
    animation-name: tagline;
    animation-delay: 1.5s;
    animation-duration: 1s;
    animation-fill-mode: both;
  }

  h2 {
    color: #401f0c;
    margin-top: 20px;
    // text-shadow: 2px 2px 2px $white;
  }

  .btn {
    margin-top: 10px;
    width: 60%;
  }

  @include app-mobile {
    padding: 0;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

#hero-logo {
  width: 600px;
  max-width: calc(100vw - 40px);
  margin-top: -40%;
  filter: drop-shadow(white 0px 2px 50px);
  z-index: 0;

  @include app-mobile {
    width: 400px;
  }
}

.scene-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  height: 100%;
  opacity: 1;
  overflow: hidden;
  z-index: -1;

  @include app-mobile {
    position: absolute;
    height: auto;
    bottom: 0;
  }
}

.parallax-layer {
  &.v-enter {
    &#hero-scene-l1 {
      transform: translateY(50vh) !important;
    }

    &#hero-scene-l2 {
      transform: translateY(80vh) !important;
    }

    &#hero-scene-l3 {
      transform: translateY(50vh) !important;
    }

    &#hero-scene-l4 {
      transform: translateY(150vh) !important;
    }

    &#hero-scene-l5 {
      transform: translateY(150vh) !important;
    }
  }

  &.v-enter,
  &.v-enter-active {
    transition: 1s;
  }
}
</style>
